import {Log, LogEvent, LogEventType, LogFilterStatus, LogStatus} from "./types";
import {REPLAYABLE_LOG_FILTER_STATUSES} from "./constants";
// @ts-ignore
import moment from 'moment'

const validHeaderNameRegExp = /^[a-zA-Z0-9][-a-zA-Z0-9]{2,4095}$/;
const validQueryParameterNameExp = /^[a-zA-Z][a-zA-Z0-9_.~-]*$/;
const validHttpBasicUsernameExp = /^[^:]{1,255}$/;
const validHttpBasicPasswordExp = /^[\x20-\x7E]{1,255}$/;
const validWebhookSigningSecretExp = /^(?! )[^ ]+(?<! )$/
const apiKeyNameRegex = /^(?! )[A-Za-z0-9\s?!-]{3,}(?<! )$/;
const emailRegex = /^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

// Whether a run-log status filter can contain finished runs, so bulk replay can act on it (CLOUDHOOKS-458, KTD-13).
export function isReplayableLogFilterStatus(logStatus: LogFilterStatus): boolean {
    return REPLAYABLE_LOG_FILTER_STATUSES.has(logStatus);
}

export function toError(error: unknown): Error {
    if (error instanceof Error) {
        return error;
    }
    return new Error(String(error));
}

export function isValidHttpHeaderName(headerName: string) {
    return validHeaderNameRegExp.test(headerName);
}

export function isValidQueryParameterName(paramName: string) {
    return validQueryParameterNameExp.test(paramName);
}

export function isValidHttpBasicUsername(userName: string) {
    return validHttpBasicUsernameExp.test(userName);
}

export function isValidHttpBasicPassword(password: string) {
    return validHttpBasicPasswordExp.test(password);
}

export function isValidWebhookSigningSecret(secret: string) {
    return validWebhookSigningSecretExp.test(secret);
}

export function isValidApiKeyName(apiKeyName: string) {
    return apiKeyNameRegex.test(apiKeyName);
}

export function isValidEmailAddress(email: string) {
    return emailRegex.test(email);
}

export function isRunSuccessful(events: LogEvent[]) {
    return !!(events && !events.some((logItem) => logItem.type === LogEventType.ERROR))
}

export function isRunWithError(events: LogEvent[]) {
    return !!(events && events.some((logItem) => logItem.type === LogEventType.ERROR))
}

export function isLogSuccessful(log: Log) {
    return log.status === LogStatus.PROCESSED && isRunSuccessful(log.events);
}

export function isLogWithError(log: Log) {
    return log.status === LogStatus.PROCESSED && isRunWithError(log.events);
}

export function areDatesWithinRange(date1: Date, date2: Date, range: number) {
    if (!date1 || !date2) return true;
    return Math.abs(date1.getTime() - date2.getTime()) < range;
}

/**
 * Source: https://stackoverflow.com/a/34817120
 *
 * @param number
 * @param decimals
 * @param dec_point
 * @param thousands_sep
 * @returns {string}
 */
export function formatNumber(number: number, decimals: number, dec_point: string, thousands_sep: string) {
    // makes sure `number` is numeric value
    number = number * 1;
    var str = number.toFixed(decimals ? decimals : 0).toString().split('.');
    var parts = [];
    for (var i = str[0].length; i > 0; i -= 3) {
        parts.unshift(str[0].substring(Math.max(0, i - 3), i));
    }
    str[0] = parts.join(thousands_sep || thousands_sep === '' ? thousands_sep : ',');

    if (str[1]) {
        str[1] = str[1].replace(/0+$/, '');
        if (str[1] === '') {
            return str[0]; // Return just the whole number part if decimal part is empty
        }
    }

    return str.join(dec_point ? dec_point : '.');
}

export function getTrialRemainingLabel(expiryDate: string | Date | null): string {
    if (expiryDate == null) return '';
    const now = moment();
    const expiry = moment(expiryDate);

    if (now.isAfter(expiry)) {
        return 'Trial expired';
    }

    // Get difference in hours
    const hoursRemaining = expiry.diff(now, 'hours', true);

    // Case: less than 45 minutes remaining
    if (hoursRemaining < 0.75) {
        return '0 hour remaining';
    }

    // Case: less than 18 hours remaining
    if (hoursRemaining < 18) {
        // Round up hours (if there's a partial hour)
        const roundedHours = Math.ceil(hoursRemaining);
        return `${roundedHours} ${roundedHours === 1 ? 'hour' : 'hours'} remaining`;
    }

    // Case: 18 hours or more remaining
    // Calculate days and round up (partial days count as full days)
    const daysRemaining = Math.ceil(hoursRemaining / 24);
    return `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining  `;
}


export function getMonthKeyFromDate(date: Date) {
    return `${date.getMonth() + 1}-${date.getFullYear()}`;
}

// The run log's display convention for a system-generated message: wrap it in surrounding double quotes. This
// mirrors how addLog renders a hook's console.log string (JSON.stringify) so platform lines — errors, the
// "Retry stopped: …" messages, and the rate-limit markers — sit uniformly alongside the hook's own output.
// Deliberately a simple wrap (matching the long-standing createErrorLogFromMessage behaviour), NOT a full
// JSON.stringify: inner quotes are left as-is, so it is the single place to change if that convention evolves.
export function quoteRunLogMessage(message: string): string {
    return `"${message}"`;
}

export function createErrorLogFromMessage(errorMessage: string): LogEvent[] {
    return [
        {
            type: LogEventType.ERROR,
            args: [quoteRunLogMessage(errorMessage)]
        }
    ]
}

export function createLogFromMessage(errorMessage: string): LogEvent[] {
    return [
        {
            type: LogEventType.LOG,
            args: [quoteRunLogMessage(errorMessage)]
        }
    ]
}
